import { RightOutlined } from '../../../components/icons'
import type { CatalogSeries } from '../types'
import { CatalogResultThumb } from './CatalogResultThumb'

function productCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'product' : 'products'}`
}

/**
 * One series/type card in the series-card grid (UID-286) — see
 * `Fictiv Platform.dc.html` ~line 981's `selCards` markup: image, name,
 * description, a spacer, "N products" count, and a "View types" affordance.
 * A real `<button>` (not a `<div role="button">`), mirroring
 * `WorkspaceCard.tsx`'s card-as-button pattern — click/Enter/Space and focus
 * come for free, no hand-rolled key handling needed.
 *
 * `imageUrl` reuses `CatalogResultThumb`'s established placeholder pattern
 * (an icon + a real, visible "No image available" caption, never a bare
 * `aria-hidden` box — UID-33's accessibility fix) rather than inventing a
 * second placeholder treatment for this vertical.
 *
 * `description`/`productCount` are independently nullable on the wire
 * (ADR-0090/0091 "omit, don't stub" — see `catalog-search-port.ts`'s
 * `CatalogSeriesSummary`): a missing description renders no paragraph at
 * all, and a missing count renders no count line — never a fabricated "0
 * products".
 */
export function SeriesCard({
  series,
  onOpen,
}: {
  readonly series: CatalogSeries
  readonly onOpen: (series: CatalogSeries) => void
}) {
  return (
    <button
      type="button"
      aria-label={`View types for ${series.name}`}
      className="catalog-series-card"
      onClick={() => onOpen(series)}
    >
      <CatalogResultThumb
        imageRef={series.imageUrl}
        name={series.name}
        className="catalog-series-image"
      />
      <div className="catalog-series-body">
        <p className="catalog-series-name">{series.name}</p>
        {series.description && <p className="catalog-series-description">{series.description}</p>}
        <div className="catalog-series-spacer" />
        <div className="catalog-series-footer">
          {series.productCount !== null && (
            <span className="catalog-series-count">{productCountLabel(series.productCount)}</span>
          )}
          <span className="catalog-series-view-types">
            View types
            <RightOutlined aria-hidden="true" />
          </span>
        </div>
      </div>
    </button>
  )
}
